import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitContact } from "@/lib/api";

const INITIAL = { name: "", email: "", subject: "", message: "" };

const fieldClass =
  "w-full border border-white/10 bg-charcoal-900/80 px-4 py-3 text-sm font-light text-sand placeholder:text-mist/50 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact(form);
      toast.success("Message sent", { description: "Thank you for reaching out — a reply will follow soon." });
      setForm(INITIAL);
    } catch {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact — Derrick Hall"
        siteName="Derrick Hall"
        description="Contact Derrick Hall, singer and vocalist."
      />
      <PageHeader
        testId="contact-header"
        eyebrow="Contact"
        title="Get in Touch"
        description="Questions, music inquiries, or a personal note — use the form below to reach Derrick directly. For performance and event inquiries, the Booking page gathers the details needed to help plan your event."
      />

      <div className="mx-auto grid max-w-5xl gap-12 px-6 pb-28 lg:grid-cols-[1fr_1.4fr]">
        <Reveal>
          <aside data-testid="contact-aside" className="space-y-8">
            <div className="border border-white/[0.08] bg-charcoal-900/60 p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Booking an event?</p>
              <p className="mt-3 text-sm font-light leading-relaxed text-mist">
                The booking inquiry form collects the date, location, event type, and
                other details needed to begin planning a performance.
              </p>
              <Link
                to="/booking"
                data-testid="contact-booking-link"
                className="group mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold"
              >
                Go to booking
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            <p className="text-sm font-light leading-relaxed text-mist">
              Messages sent through this form go directly to Derrick. A personal response
              will follow.
            </p>
          </aside>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={onSubmit} data-testid="contact-form" className="grid gap-6">
            <div>
              <label htmlFor="name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
                Name <span className="text-gold">*</span>
              </label>
              <Input id="name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} className={fieldClass} placeholder="Your name" />
            </div>
            <div>
              <label htmlFor="email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
                Email <span className="text-gold">*</span>
              </label>
              <Input id="email" type="email" data-testid="contact-input-email" required value={form.email} onChange={set("email")} className={fieldClass} placeholder="you@example.com" />
            </div>
            <div>
              <label htmlFor="subject" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
                Subject <span className="text-gold">*</span>
              </label>
              <Input id="subject" data-testid="contact-input-subject" required value={form.subject} onChange={set("subject")} className={fieldClass} placeholder="What is this about?" />
            </div>
            <div>
              <label htmlFor="message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
                Message <span className="text-gold">*</span>
              </label>
              <Textarea id="message" data-testid="contact-input-message" required rows={6} value={form.message} onChange={set("message")} className={fieldClass} placeholder="Write your message here…" />
            </div>
            <div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={sending}
                className="bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal-950 transition-colors duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </>
  );
}
